import React from 'react'

import { useTranslation } from 'react-i18next'

import { FieldLabel } from '@cash-design-system/react'
import {
  isValidBankAccountNumberAus,
  isValidBankAccountNumberCad,
  isValidBankAccountNumberGb,
  isValidBankAccountNumberUsa,
} from '@cash-web/shared-util-validation'

import { FormMaskedInput } from '../FormMaskedInput'

export type AccountNumberProps = {
  name: string
  region: 'US' | 'GB' | 'CA' | 'AU'
  label?: string
}

const regionConfig = {
  AU: {
    format: '##########',
    validate: isValidBankAccountNumberAus,
  },
  CA: {
    format: '############',
    validate: isValidBankAccountNumberCad,
  },
  GB: {
    format: '#########',
    validate: isValidBankAccountNumberGb,
  },
  US: {
    format: '#################',
    validate: isValidBankAccountNumberUsa,
  },
}

export const AccountNumber: React.FC<AccountNumberProps> = ({ name, region, label, ...rest }) => {
  const { t } = useTranslation()

  const { format, validate } = regionConfig[region]

  return (
    <FieldLabel label={label ?? t('Account number', { context: 'label for account number form field' })}>
      <FormMaskedInput
        {...rest}
        name={name}
        placeholder={t('Full account number', { context: 'placeholder for account number form field' })}
        format={format}
        rules={{
          required: {
            value: true,
            message: t('Enter your account number.', { context: 'error message for not entering account number' }),
          },
          validate: value =>
            validate(value) ||
            t('Invalid account number.', { context: 'error message for entering an invalid account number' }),
        }}
      />
    </FieldLabel>
  )
}
